import { apiClient } from '../index';
import { PaginationParams, PaginationResponse, ApiResponse } from '../types';
import { logger } from '../../logger';


// MAWB 관련 타입 정의
export interface MawbData {
  mawbRef?: string;
  mawbNo?: string;
  fltNo?: string;
  station?: string;
  dstn?: string;
  depArea?: string;
  area?: string;
  depDate?: string;
  arrDate?: string;
  dueYn?: string;
  dpcCode?: string;
  depTime?: string;
  arrTime?: string;
  transType?: string; // transType 필드 추가
  companyCode?: string;  // 소속 센터 코드
  companyName?: string;  // 소속 센터 이름
}

export interface MawbListParams extends PaginationParams {
  startDate?: string | null;    // 출발일
  endDate?: string | null;
  mawbNo?: string | null;       // MAWB 번호
  station?: string | null;      // 출발지
  dstn?: string | null;         // 도착지
  depArea?: string | null;      // 출발 공항
  area?: string | null;         // 도착공항
  fltNo?: string | null;        // 항공기 편명
  companyCode?: string | null;  // 센터(회사) 코드 - superUser 전용 필터
}

// API 응답 구조에 맞는 타입 정의
export interface MawbListApiResponse {
  status: number;
  success: boolean;
  message: string;
  data: {
    content: MawbData[];
    totalCount: number;
    pageCount: number;
    page: number;
    size: number;
  };
}

export interface MawbCreateData {
  mawbNo: string;        // MAWB 번호
  depDate: string;       // 출발일
  depTime: string;       // 출발시간
  arrDate: string;       // 도착일
  arrTime: string;       // 도착시간
  fltNo: string;         // 항공기 편명
  station: string;       // 출발지
  dstn: string;          // 도착지
  depArea: string;       // 출발 공항
  transType: string;     // 운송타입
  area: string;          // 도착공항
  companyCode?: string;  // 센터(회사) 코드 - superUser만 전송
}

export interface MawbUpdateData extends Partial<MawbCreateData> {
  refNo: string;
}

export interface MawbUpdatePostData {
  mawbRef: string;
  mawbNo?: string;
  depDate?: string;
  depTime?: string;
  arrDate?: string;
  arrTime?: string;
  fltNo?: string;
  dstn?: string;
  station?: string;
  depArea?: string;
  transType?: string;
  area?: string;
  companyCode?: string;  // 센터(회사) 코드 - superUser만 전송
}

// MAWB 서비스 클래스
export class MawbService {
  /**
   * MAWB 목록 조회
   * @param params 검색 및 페이지네이션 파라미터
   * @returns MAWB 목록과 페이지네이션 정보
   */
  static async getMawbList(params: MawbListParams): Promise<PaginationResponse<MawbData>> {
    // POST 요청으로 변경 - request body에 데이터 전송
    const requestBody = {
      startDate: params.startDate || "",    // 출발일
      mawbNo: params.mawbNo || "",         // MAWB 번호
      station: params.station || "",       // 출발지
      dstn: params.dstn || "",             // 도착지
      depArea: params.depArea || "",       // 출발 공항
      area: params.area || "",             // 도착공항
      fltNo: params.fltNo || "",           // 항공기 편명
      companyCode: params.companyCode || "",  // 센터(회사) - superUser 전용
      limit: params.limit.toString(),
      page: params.page.toString()
    };

    try {
      logger.info('📤 POST 요청 데이터:', requestBody);
      const response = await apiClient.post<MawbListApiResponse>('/mawb/list', requestBody);
      logger.info('✅ getMawbList 성공:', response);
      
      // API 응답 구조를 PaginationResponse 형태로 변환
      const transformedResponse: PaginationResponse<MawbData> = {
        data: response.data.content,
        total: response.data.totalCount,
        page: response.data.page,
        limit: response.data.size,
        totalPages: response.data.pageCount
      };
      
      logger.info('🔄 변환된 응답:', transformedResponse);
      return transformedResponse;
    } catch (error) {
      logger.error('❌ getMawbList 실패:', error);
      throw error;
    }
  }

  /**
   * MAWB 상세 조회
   * @param refNo MAWB 참조 번호
   * @returns MAWB 상세 정보
   */
  static async getMawbDetail(refNo: string): Promise<MawbData> {
    try {
      logger.info('🔍 MAWB 상세 조회 요청:', refNo);
      const response = await apiClient.get<MawbData>(`/mawb/${encodeURIComponent(refNo)}`);
      logger.info('✅ MAWB 상세 조회 성공:', response);
      return response;
    } catch (error) {
      logger.error('❌ MAWB 상세 조회 실패:', error);
      throw error;
    }
  }

  /**
   * MAWB 생성
   * @param data MAWB 생성 데이터
   * @returns 생성된 MAWB 정보
   */
  static async createMawb(data: MawbCreateData): Promise<MawbData> {
    try {
      logger.info('📤 MAWB 생성 요청 데이터:', data);
      const response = await apiClient.post<MawbData>('/mawb/create', data);
      logger.info('✅ MAWB 생성 성공:', response);
      return response;
    } catch (error) {
      logger.error('❌ MAWB 생성 실패:', error);
      throw error;
    }
  }

  /**
   * MAWB 수정
   * @param refNo MAWB 참조 번호
   * @param data 수정할 데이터
   * @returns 수정된 MAWB 정보
   */
  static async updateMawb(refNo: string, data: Partial<MawbCreateData>): Promise<MawbData> {
    try {
      logger.info('📝 MAWB 수정 요청 데이터:', { refNo, data });
      const response = await apiClient.put<MawbData>(`/mawb/${encodeURIComponent(refNo)}`, data);
      logger.info('✅ MAWB 수정 성공:', response);
      return response;
    } catch (error) {
      logger.error('❌ MAWB 수정 실패:', error);
      throw error;
    }
  }

  /**
   * MAWB 부분 수정 (PATCH)
   * @param refNo MAWB 참조 번호
   * @param data 수정할 데이터
   * @returns 수정된 MAWB 정보
   */
  static async patchMawb(refNo: string, data: Partial<MawbCreateData>): Promise<MawbData> {
    try {
      logger.info('🩹 MAWB 부분 수정 요청 데이터:', { refNo, data });
      const response = await apiClient.patch<MawbData>(`/mawb/${encodeURIComponent(refNo)}`, data);
      logger.info('✅ MAWB 부분 수정 성공:', response);
      return response;
    } catch (error) {
      logger.error('❌ MAWB 부분 수정 실패:', error);
      throw error;
    }
  }

  /**
   * MAWB 삭제
   * @param mawbRef MAWB 참조 번호
   * @returns 삭제 결과
   */
  static async deleteMawb(mawbRef: string): Promise<ApiResponse<null>> {
    try {
      logger.info('📤 MAWB 삭제 요청 데이터:', { mawbRef });
      const response = await apiClient.post<ApiResponse<null>>('/mawb/delete', { mawbRef });
      logger.info('✅ MAWB 삭제 성공:', response);
      return response;
    } catch (error) {
      logger.error('❌ MAWB 삭제 실패:', error);
      throw error;
    }
  }

  /**
   * MAWB 일괄 삭제
   * @param refNos 삭제할 MAWB 참조 번호 배열
   * @returns 삭제 결과
   */
  static async deleteMawbBatch(refNos: string[]): Promise<ApiResponse<{ deletedCount: number }>> {
    return apiClient.post<ApiResponse<{ deletedCount: number }>>('/mawb/batch-delete', { refNos });
  }

  /**
   * MAWB 상태 변경
   * @param refNo MAWB 참조 번호
   * @param status 새로운 상태
   * @returns 변경된 MAWB 정보
   */
  static async updateMawbStatus(refNo: string, status: string): Promise<MawbData> {
    return apiClient.patch<MawbData>(`/mawb/${encodeURIComponent(refNo)}/status`, { status });
  }

  /**
   * MAWB 통계 조회
   * @param params 통계 조회 파라미터
   * @returns MAWB 통계 정보
   */
  static async getMawbStats(params?: {
    startDate?: string;
    endDate?: string;
    region?: string;
  }): Promise<{
    total: number;
    byRegion: Record<string, number>;
    byStatus: Record<string, number>;
    byDate: Record<string, number>;
  }> {
    const searchParams = new URLSearchParams();

    if (params?.startDate) searchParams.append('startDate', params.startDate);
    if (params?.endDate) searchParams.append('endDate', params.endDate);
    if (params?.region) searchParams.append('region', params.region);

    const queryString = searchParams.toString();
    return apiClient.get(`/mawb/stats${queryString ? `?${queryString}` : ''}`);
  }

  /**
   * MAWB 엑셀 내보내기
   * @param params 내보낼 데이터 필터
   * @returns 엑셀 파일 다운로드 URL
   */
  static async exportMawbToExcel(params?: MawbListParams): Promise<{ downloadUrl: string }> {
    const searchParams = new URLSearchParams();

    if (params?.mawbNo) searchParams.append('mawbNo', params.mawbNo);
    if (params?.dstn) searchParams.append('dstn', params.dstn);
    if (params?.depArea) searchParams.append('depArea', params.depArea);
    if (params?.area) searchParams.append('area', params.area);
    if (params?.startDate) searchParams.append('startDate', params.startDate);
    if (params?.endDate) searchParams.append('endDate', params.endDate);
    if (params?.fltNo) searchParams.append('fltNo', params.fltNo);

    const queryString = searchParams.toString();
    return apiClient.get(`/mawb/export${queryString ? `?${queryString}` : ''}`);
  }

  static async updateMawbByPost(data: MawbUpdatePostData): Promise<MawbData> {
    try {
      logger.info('📝 MAWB 수정 (POST) 요청 데이터:', data);
      const response = await apiClient.post<MawbData>('/mawb/update', data);
      logger.info('✅ MAWB 수정 (POST) 성공:', response);
      return response;
    } catch (error) {
      logger.error('❌ MAWB 수정 (POST) 실패:', error);
      throw error;
    }
  }
}
